<script setup lang="ts">
import {
  IconEmoji,
  IconRespond,
  IconEdit,
  SlideTransition,
  useDisclosure,
  globalOverlayContainer,
  userInjectionKey,
  guest,
} from '#shared'
import { computed, inject, toRef, unref, useTemplateRef } from 'vue'
import type { Emoji } from '#features/emoji-selection'
import { EmojiSelect } from '#features/emoji-selection'
import { useTriggerMessageEdit } from '#features/message-editing'
import { useTriggerRespond } from '#features/message-responds'
import type { Message } from '#entities/message'
import { useReactionActionEmojis } from '../lib/use-reaction-action-emojis'
import { useToggleReaction } from '../lib/use-toggle-reaction'

const { message, isMessageHovered } = defineProps<{ message: Message; isMessageHovered: boolean }>()

const actionEmojis = useReactionActionEmojis()

const triggerRespond = useTriggerRespond()
const onRespondAction = () => {
  triggerRespond(message)
}

const currentUser = inject(userInjectionKey)
const isEditActionAvailable = computed(() => {
  const currentUserVal = unref(currentUser)
  if (!currentUserVal || currentUserVal === guest) return false
  return currentUserVal.id === message.sender.id
})
const triggerMessageEdit = useTriggerMessageEdit()
const onEditAction = () => {
  triggerMessageEdit(message)
}

const newReactionButtonEl = useTemplateRef('newReactionButtonEl')
const newReactionSelectDisclosure = useDisclosure()

const toggleReaction = useToggleReaction(toRef(() => message))

const onReactionSelect = (emoji: Emoji) => {
  toggleReaction(emoji)
  newReactionSelectDisclosure.close()
}

const emojiSelectPositions = computed(() => ({
  verticalPosition: 'top-outside' as const,
  horizontalPosition: 'right-inside' as const,
  verticalOffset: -10,
}))

const isShown = computed(() => isMessageHovered || newReactionSelectDisclosure.isOpen)
</script>

<template>
  <div :class="[$style.root, { [$style.rootShown]: isShown }]">
    <div :class="$style.group">
      <button
        v-for="emoji in actionEmojis"
        :key="emoji.id"
        :class="[$style.action, $style.actionWithEmoji]"
        @click="onReactionSelect(emoji)"
      >
        {{ emoji.char }}
      </button>

      <button
        key="new-reaction"
        :class="[$style.action, $style.actionWithIcon]"
        ref="newReactionButtonEl"
        title="More reactions"
        @click="newReactionSelectDisclosure.toggle"
      >
        <IconEmoji :class="$style.newReactionIcon" />
      </button>
    </div>

    <div :class="$style.separator" />

    <div :class="$style.group">
      <button v-if="isEditActionAvailable" :class="[$style.action, $style.actionWithIcon]" @click="onEditAction">
        <IconEdit :class="$style.editIcon" />
      </button>

      <button :class="[$style.action, $style.actionWithIcon]" @click="onRespondAction">
        <IconRespond />
      </button>
    </div>

    <Teleport :to="globalOverlayContainer" v-if="isShown">
      <SlideTransition :offsetX="-10">
        <EmojiSelect
          :class="$style.newReactionSelect"
          :isOpen="newReactionSelectDisclosure.isOpen"
          :targetEl="newReactionButtonEl"
          v-bind="emojiSelectPositions"
          @select="onReactionSelect"
          @close="newReactionSelectDisclosure.close"
        />
      </SlideTransition>
    </Teleport>
  </div>
</template>

<style lang="scss" module>
.root {
  display: flex;
  align-items: center;
  border: 1px solid #303035;
  background-color: #1f1c1b;
  border-radius: 4px;
  position: absolute;
  top: 0;
  right: 10px;
  z-index: 1;
  transform: translateY(-50%);
  opacity: 0;
}

.rootShown {
  opacity: 1;
}

.separator {
  margin: 0 2px;
  height: 26px;
  width: 1px;
  background-color: #303035;
}

.group {
  display: flex;
}

.action {
  height: 26px;
  padding: 3px;
  width: auto;
  display: flex;
  flex-shrink: 0;
  border-radius: 2px;
  border: none;
  background-color: transparent;
  border: none;
  cursor: pointer;

  &:hover {
    background-color: #303035;
  }
}

.actionWithIcon {
  color: #ffffff80;

  &:hover {
    color: white;
  }
}

.actionWithEmoji {
  font-family: 'Noto Color Emoji', serif;
  font-size: 16px;
}

.newReactionIcon {
  width: 20px;
  height: 20px;
}

.editIcon {
  width: 20px;
  height: 20px;
}
</style>
